import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './useAuth';

export interface ChatSession {
  id: string;
  prompt: string;
  state: string; // 'STARTING' | 'SEARCHING' | 'DONE' | 'ERROR'
  finalResult: string;
  sources?: Array<{ document_id: string; filename: string; page?: number; score: number }>;
  latency_ms?: number;
  routed_tier?: string;
  error?: string;
}

export interface Conversation {
  id: string;
  title: string;
  selectedDocumentIds: string[]; // List of documents selected for this conversation
  messages: ChatSession[];
  created_at: string;
}

interface ChatHistoryCtx {
  conversations: Conversation[];
  activeId: string | null;
  createConversation: (selectedDocumentIds?: string[], prompt?: string) => string;
  deleteConversation: (id: string) => void;
  updateMessages: (id: string, messages: ChatSession[], selectedDocumentIds?: string[], title?: string) => void;
  setActiveId: (id: string | null) => void;
}

const ChatHistoryContext = createContext<ChatHistoryCtx | null>(null);

export function ChatHistoryProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Load conversations from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('erixone_conversations') ?? localStorage.getItem('ericone_conversations');
    if (stored) {
      try {
        setConversations(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse stored conversations', e);
      }
    }
  }, []);

  // Helper to save to localStorage
  const persistConversations = (updated: Conversation[]) => {
    const stripped = updated.map(c => ({
      ...c,
      messages: c.messages.map(m => ({
        id: m.id,
        prompt: m.prompt,
        state: m.state,
        finalResult: m.finalResult,
        sources: m.sources,
        latency_ms: m.latency_ms,
        error: m.error
      }))
    }));
    localStorage.setItem('erixone_conversations', JSON.stringify(stripped));
  };

  useEffect(() => {
    if (!isLoggedIn) {
      setConversations([]);
      setActiveId(null);
    }
  }, [isLoggedIn]);

  const createConversation = useCallback((selectedDocumentIds: string[] = [], prompt?: string) => {
    const id = `session_${Date.now()}`;
    const newConv: Conversation = {
      id,
      title: prompt ? (prompt.length > 30 ? prompt.substring(0, 30) + '...' : prompt) : 'New Conversation',
      selectedDocumentIds,
      messages: [],
      created_at: new Date().toISOString()
    };
    
    setConversations(prev => {
      const updated = [newConv, ...prev];
      persistConversations(updated);
      return updated;
    });
    
    setActiveId(id);
    return id;
  }, []);

  const deleteConversation = useCallback((id: string) => {
    setConversations(prev => {
      const updated = prev.filter(c => c.id !== id);
      persistConversations(updated);
      return updated;
    });
    
    setActiveId(prevId => prevId === id ? null : prevId);
  }, []);

  const updateMessages = useCallback((id: string, messages: ChatSession[], selectedDocumentIds?: string[], title?: string) => {
    setConversations(prev => {
      const updated = prev.map(c => {
        if (c.id === id) {
          return {
            ...c,
            messages,
            selectedDocumentIds: selectedDocumentIds !== undefined ? selectedDocumentIds : c.selectedDocumentIds,
            title: title !== undefined ? title : (messages[0]?.prompt ? (messages[0].prompt.length > 30 ? messages[0].prompt.substring(0, 30) + '...' : messages[0].prompt) : c.title)
          };
        }
        return c;
      });

      persistConversations(updated);
      return updated;
    });
  }, []);

  return (
    <ChatHistoryContext.Provider value={{
      conversations,
      activeId,
      createConversation,
      deleteConversation,
      updateMessages,
      setActiveId
    }}>
      {children}
    </ChatHistoryContext.Provider>
  );
}

export function useChatHistory() {
  const ctx = useContext(ChatHistoryContext);
  if (!ctx) throw new Error('useChatHistory must be inside ChatHistoryProvider');
  return ctx;
}
